import React, { useState } from 'react';
import { ChevronLeftUniversal, TrashCanOutlined } from '@duxton-web/icons';
import {
  Button,
  ConfigProvider,
  Modal,
  notification,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { useQueryClient } from '@tanstack/react-query';

import { useDeleteProductEntity } from '@/hooks/usePlannerQueries';

const DeleteEntityModalTitle = ({
  productEntityName,
  loading,
  onClose,
}: {
  productEntityName: string;
  loading: boolean;
  onClose: () => void;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
      }}
    >
      <ConfigProvider
        theme={{
          components: {
            Typography: {
              titleMarginBottom: 0,
            },
          },
        }}
      >
        <Button
          data-testid="delete-entity-back"
          shape="circle"
          onClick={onClose}
          size="small"
          ghost
          icon={<ChevronLeftUniversal />}
          disabled={loading}
        />
        <Tooltip title={`Delete ${productEntityName}`}>
          <Typography.Title
            level={3}
            ellipsis={{
              rows: 1,
            }}
          >{`Delete ${productEntityName}`}</Typography.Title>
        </Tooltip>
      </ConfigProvider>
    </div>
  );
};

const DeleteEntityModal = ({
  productEntityName,
  open = false,
  onOk = () => {},
  onCancel = () => {},
}: {
  productEntityName: string;
  open?: boolean;
  onOk?: () => void;
  onCancel?: () => void;
}) => {
  const queryClient = useQueryClient();

  const { isPending, mutate } = useDeleteProductEntity({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['planner', 'get-product-entities'],
      });

      onOk();

      notification.success({
        message: 'Product entity deleted',
        description: `Product entity ${productEntityName} was deleted successfully`,
      });
    },
    onError: (_) => {
      notification.error({
        message: 'Error deleting product entity',
        description: `Error deleting product entity ${productEntityName}`,
      });
    },
  });

  return (
    <Modal
      data-testid="delete-entity-modal"
      closable={false}
      maskClosable={false}
      open={open}
      centered
      title={
        <DeleteEntityModalTitle
          productEntityName={productEntityName}
          loading={isPending}
          onClose={() => {
            onCancel();
          }}
        />
      }
      onCancel={() => {
        onCancel();
      }}
      cancelButtonProps={{
        size: 'small',
        disabled: isPending,
      }}
      okButtonProps={{
        danger: true,
        size: 'small',
        disabled: isPending,
        loading: isPending,
      }}
      mask={false}
      okText="Delete"
      onOk={() => mutate(productEntityName)}
      width={700}
    >
      <Typography.Text>
        Are you sure you want to delete &quot;{productEntityName}&quot;.
        <br />
        This will remove the product name metadata from all variables it&apos;s
        attached to.
      </Typography.Text>
    </Modal>
  );
};

interface DeleteEntityProps {
  productEntityName: string;
  count: number;
  onDeleteClick?: () => void;
  onCancel?: () => void;
  onOk?: () => void;
}

const DeleteEntity = ({
  productEntityName,
  count,
  onDeleteClick = () => {},
  onCancel = () => {},
  onOk = () => {},
}: DeleteEntityProps) => {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <>
      <Button
        data-testid="delete-entity-button"
        size="small"
        shape="circle"
        type="text"
        onClick={() => {
          onDeleteClick();
          setOpen(true);
        }}
        disabled={!!count}
        danger
        icon={<TrashCanOutlined />}
      />
      <DeleteEntityModal
        productEntityName={productEntityName}
        open={open}
        onOk={() => {
          setOpen(false);

          onOk();
        }}
        onCancel={() => {
          setOpen(false);

          onCancel();
        }}
      />
    </>
  );
};

export default DeleteEntity;
